import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';
import { router } from 'svelte-spa-router';

// WARNING: do NOT import any of the other stores!
import { Constants } from '@/data/constants';
import { expansionOrder } from '@/data/expansion';
import { professionCooldowns, professionWorkOrders } from '@/data/professions/cooldowns';
import { Language } from '@/enums/language';
import { sharedState } from '@/shared/state/shared.svelte';
import { getNumberKeyedEntries } from '@/utils/get-number-keyed-entries';
import getTransmogClassMask from '@/utils/get-transmog-class-mask';
import { hashObject } from '@/utils/hash-object.svelte';
import type { ExpansionData } from '@/types';
import type { Task } from '@/types/tasks';

import { browserState } from './browser.svelte';
import type { Settings } from '../stores/settings/types';
import { delveMap } from '@/data/delve';

const languageToSubdomain: Record<Language, string> = {
    [Language.deDE]: 'de',
    [Language.enUS]: 'www',
    [Language.esES]: 'es',
    [Language.esMX]: 'es',
    [Language.frFR]: 'fr',
    [Language.itIT]: 'it',
    [Language.ruRU]: 'ru',
    [Language.ptBR]: 'pt',
};

function createSettingsState() {
    // let hashTimer: ReturnType<typeof setInterval> | null = null;
    let settingsHash = '';

    let settings = $state<Settings>();
    let saving = $state(0);

    const saveData = async () => {
        saving = 1;

        const response = await fetch('/api/settings', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                RequestVerificationToken: sharedState.xsrf,
            },
            body: JSON.stringify({ settings }),
        });

        if (response.ok) {
            // const json = await response.json();
            // const settings = json.settings as Settings;
            // this.set(settings);
            saving = 2;
        }
    };
    const debouncedSaveData = debounce(saveData, 1500);

    const useAccountTags = $derived.by(() =>
        Object.values(settings.accounts).some((account) => !!account.tag)
    );

    const activeView = $derived(
        (router.location === '/'
            ? settings.views.find((view) => view.id === browserState.current.home.activeView)
            : settings.views[0]) || settings.views[0]
    );

    const commonColspan = $derived.by(() => {
        return (
            activeView.commonFields.length +
            (activeView.commonFields.indexOf('accountTag') >= 0 ? (useAccountTags ? 0 : -1) : 0)
        );
    });

    const customTaskMap = $derived(
        Object.fromEntries(
            (settings.customTasks || []).map((task) => [
                task.key,
                {
                    ...task,
                    chores: [
                        {
                            key: `${task.key}1`,
                            name: task.name,
                            questIds: task.questIds,
                            questReset: task.questReset,
                        },
                    ],
                } as Task,
                // task
            ])
        )
    );

    const derivedTransmogClassMask = $derived.by(() => getTransmogClassMask());

    const allTasks = $derived.by(() => uniq(settings.views.flatMap((view) => view.homeTasks)));

    return {
        set(newSettings: Settings) {
            if (!newSettings) {
                console.warn('settings data is invalid!');
                return;
            }

            // if (hashTimer !== null) {
            //     clearInterval(hashTimer);
            //     hashTimer = null;
            // }

            saving = 0;
            settings = newSettings;

            if (sharedState.public) {
                return;
            }

            if (settingsHash === '') {
                settingsHash = hashObject(newSettings, []);
            }

            for (const professionCooldown of [...professionCooldowns, ...professionWorkOrders]) {
                if (newSettings.professions.cooldowns[professionCooldown.key] === undefined) {
                    newSettings.professions.cooldowns[professionCooldown.key] = true;
                }
            }

            for (const [poiId, delve] of getNumberKeyedEntries(delveMap)) {
                for (const [story, defaultRanking] of Object.entries(delve.storyRanks)) {
                    const storyKey = `${poiId}:${story}`;
                    if (newSettings.delveRankings[storyKey] === undefined) {
                        newSettings.delveRankings[storyKey] = defaultRanking;
                    }
                }
            }

            if (Object.keys(newSettings.professions.collectingCharactersV2 || {}).length === 0) {
                for (const [professionId, characterId] of getNumberKeyedEntries(
                    newSettings.professions.collectingCharacters || {}
                )) {
                    newSettings.professions.collectingCharactersV2[professionId] = [characterId];
                }
                newSettings.professions.collectingCharacters = {};
            }

            // hashTimer = setInterval(async () => {
            //     const settingsData = $state.snapshot(settings);
            //     const newSettingsHash = hashObject(settingsData);
            //     if (newSettingsHash !== settingsHash) {
            //         settingsHash = newSettingsHash;
            //         await debouncedSaveData();
            //     }
            // }, 1000);
        },
        saveData() {
            debouncedSaveData();
        },
        get value() {
            return settings;
        },
        get saving() {
            return saving;
        },
        get activeView() {
            return activeView;
        },
        get allTasks() {
            return allTasks;
        },
        get commonColspan() {
            return commonColspan;
        },
        get customTaskMap() {
            return customTaskMap;
        },
        get useAccountTags() {
            return useAccountTags;
        },
        get expansions(): ExpansionData[] {
            return expansionOrder.filter(
                (exp) => !settings.collections.hideFuture || exp.id <= Constants.expansion
            );
        },
        get wowheadBaseUrl(): string {
            return `${languageToSubdomain[settings.general.language]}.wowhead.com`;
        },
        get transmogClassMask(): number {
            return derivedTransmogClassMask;
        },
    };
}

export const settingsState = createSettingsState();
settingsState.set(
    JSON.parse(document.getElementById('app').getAttribute('data-settings')) as Settings
);
